/**
 * Interview Video Player
 *
 * Custom HTML5 video player for viewing recorded interview sessions.
 * Features:
 * - Play/Pause/Seek controls
 * - Question-based timestamp navigation
 * - Conversation transcript display
 * - Download button (VA's own videos only)
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Play, Pause, Volume2, VolumeX, Download, Maximize2,
  SkipBack, SkipForward, MessageSquare,
  ChevronDown, ChevronUp
} from 'lucide-react';

function formatTime(secs) {
  if (!secs || isNaN(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const InterviewVideoPlayer = ({
  videoUrl,
  conversationLog = [],
  vaName          = '',
  canDownload     = false,
  className       = '',
}) => {
  const videoRef = useRef(null);

  const [isPlaying,    setIsPlaying]    = useState(false);
  const [isMuted,      setIsMuted]      = useState(false);
  const [currentTime,  setCurrentTime]  = useState(0);
  const [duration,     setDuration]     = useState(0);
  const [volume,       setVolume]       = useState(1);
  const [showTranscript, setShowTranscript] = useState(false);

  // Questions extracted from conversation log
  const aiTurns = conversationLog.filter(e => e.type === 'ai');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTimeUpdate  = () => setCurrentTime(video.currentTime);
    const onDuration    = () => setDuration(video.duration);
    const onPlay        = () => setIsPlaying(true);
    const onPause       = () => setIsPlaying(false);
    const onEnded       = () => setIsPlaying(false);

    video.addEventListener('timeupdate',    onTimeUpdate);
    video.addEventListener('loadedmetadata', onDuration);
    video.addEventListener('play',          onPlay);
    video.addEventListener('pause',         onPause);
    video.addEventListener('ended',         onEnded);

    return () => {
      video.removeEventListener('timeupdate',    onTimeUpdate);
      video.removeEventListener('loadedmetadata', onDuration);
      video.removeEventListener('play',          onPlay);
      video.removeEventListener('pause',         onPause);
      video.removeEventListener('ended',         onEnded);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    isPlaying ? v.pause() : v.play();
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect  = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    v.currentTime = ratio * duration;
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    const v = videoRef.current;
    if (v) v.volume = val;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const skipSeconds = (secs) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(duration, v.currentTime + secs));
  };

  const toggleFullscreen = () => {
    const wrapper = videoRef.current?.parentElement;
    if (!wrapper) return;
    if (!document.fullscreenElement) {
      wrapper.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const progressPct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`bg-white rounded-xl shadow border border-gray-100 overflow-hidden ${className}`}>

      {/* Video element */}
      <div className="relative bg-black aspect-video">
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            className="w-full h-full object-contain"
            preload="metadata"
            playsInline
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400">
            <p className="text-sm">Video unavailable</p>
          </div>
        )}

        {/* Overlay controls */}
        {videoUrl && (
          <div
            className="absolute inset-0 group flex flex-col justify-end"
            onClick={togglePlay}
          >
            {/* Large play icon in center */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-14 h-14 rounded-full bg-black/50 flex items-center justify-center">
                  <Play className="w-7 h-7 text-white ml-1" />
                </div>
              </div>
            )}

            {/* Bottom controls */}
            <div
              className="opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/70 to-transparent px-4 pb-3 pt-8"
              onClick={e => e.stopPropagation()}
            >
              {/* Seek bar */}
              <div
                className="w-full h-1.5 bg-white/30 rounded-full cursor-pointer mb-3"
                onClick={handleSeek}
              >
                <div
                  className="h-1.5 bg-emerald-400 rounded-full pointer-events-none"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="flex items-center gap-3">
                <button onClick={() => skipSeconds(-10)} className="text-white hover:text-emerald-300 transition-colors">
                  <SkipBack className="w-4 h-4" />
                </button>
                <button onClick={togglePlay} className="text-white hover:text-emerald-300 transition-colors">
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
                <button onClick={() => skipSeconds(10)} className="text-white hover:text-emerald-300 transition-colors">
                  <SkipForward className="w-4 h-4" />
                </button>

                <button onClick={toggleMute} className="text-white hover:text-emerald-300 ml-1">
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <input
                  type="range" min="0" max="1" step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-16 accent-emerald-400"
                />

                <span className="text-white/70 text-xs ml-auto">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                <button onClick={toggleFullscreen} className="text-white hover:text-emerald-300">
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Meta + Score */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <p className="font-semibold text-gray-800 text-sm">
            {vaName ? `${vaName}'s Interview` : 'AI Interview Recording'}
          </p>
        </div>
        {canDownload && videoUrl && (
          <a
            href={videoUrl}
            download
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-emerald-600 border border-gray-200 rounded-lg px-3 py-1.5 hover:border-emerald-300 transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download
          </a>
        )}
      </div>

      {/* Accordion: Transcript */}
      <div className="border-b border-gray-100">
        <button
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          onClick={() => setShowTranscript(p => !p)}
        >
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-400" />
            Conversation Transcript ({aiTurns.length} questions)
          </span>
          {showTranscript ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </button>
        {showTranscript && (
          <div className="px-4 pb-4 space-y-3 max-h-64 overflow-y-auto">
            {conversationLog.map((entry, i) => (
              <div
                key={i}
                className={`text-xs rounded-lg p-3 ${entry.type === 'ai' ? 'bg-gray-50 text-gray-700' : 'bg-emerald-50 text-emerald-800 ml-6'}`}
              >
                <p className="font-semibold mb-1 text-[10px] uppercase tracking-wide opacity-60">
                  {entry.type === 'ai' ? `Round ${entry.round} — Interviewer` : 'Your Response'}
                </p>
                <p className="leading-relaxed">{entry.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewVideoPlayer;
